// src/components/AuctionForm.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  CircularProgress,
  Tooltip,
  SelectChangeEvent,
  InputAdornment,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import auctionService from "../../../services/auctionService";
import { GISProperty } from "../../../types/gis";
import api from "../../../services/api";
import { API_ENDPOINTS } from "../../../constants/API_ENDPOINTS";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_DIMENSION = 8000; // 8000px (allow nearly all modern smartphone photos)
const MIN_IMAGE_DIMENSION = 200; // 200px

export interface AuctionFormValues {
  title: string;
  description: string;
  starting_price: number;
  min_increment: number;
  end_time: Date | null;
  category: string;
  quantity: number;
  images: string[];
  video?: string;
  parcel_id?: string;
  current_price?: number;
}

interface AuctionFormProps {
  initialValues: AuctionFormValues;
  onSubmit: (
    values: AuctionFormValues,
    videoFile?: File | null
  ) => Promise<void>;
  isEdit?: boolean;
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  gisProperties?: GISProperty[];
}

const AuctionForm: React.FC<AuctionFormProps> = ({
  initialValues,
  onSubmit,
  isEdit = false,
  loading = false,
  error,
  success,
  gisProperties = [],
}) => {
  const [form, setForm] = useState<AuctionFormValues>({
    ...initialValues,
    quantity: initialValues.quantity ?? 1,
  });

  const formatNumberForDisplay = (num: number | undefined | null): string => {
    if (num === null || num === undefined) return "";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const [displayStartingPrice, setDisplayStartingPrice] = useState(
    formatNumberForDisplay(initialValues.starting_price)
  );
  const [displayMinIncrement, setDisplayMinIncrement] = useState(
    formatNumberForDisplay(initialValues.min_increment)
  );
  const [displayCurrentPrice, setDisplayCurrentPrice] = useState(
    formatNumberForDisplay(initialValues.current_price)
  );

  const [images, setImages] = useState<string[]>(initialValues.images || []);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryDialog, setCategoryDialog] = useState<
    null | "create" | "edit" | "delete"
  >(null);
  const [categoryNameInput, setCategoryNameInput] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryMsg, setCategoryMsg] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get(API_ENDPOINTS.CATEGORIES);
      const names = (res.data || [])
        .map((c: { name: string }) => c.name)
        .filter(Boolean);
      setCategories(names);
      setForm((prev) => {
        if (prev.category && names.includes(prev.category)) return prev;
        if (names.includes("Other")) return { ...prev, category: "Other" };
        if (names.length) return { ...prev, category: names[0] };
        return prev;
      });
    } catch (err) {
      console.error("Failed to load categories", err);
      setCategories((prev) =>
        prev.length
          ? prev
          : [
              "Electronics",
              "Fashion",
              "Home",
              "Sports",
              "Collectibles",
              "Art",
              "Books",
              "Other",
            ]
      );
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/heic",
    "image/jpg",
  ];

  useEffect(() => {
    setForm({ ...initialValues, quantity: initialValues.quantity ?? 1 });
    setImages(initialValues.images || []);
    setDisplayStartingPrice(
      formatNumberForDisplay(initialValues.starting_price)
    );
    setDisplayMinIncrement(formatNumberForDisplay(initialValues.min_increment));
    setDisplayCurrentPrice(formatNumberForDisplay(initialValues.current_price));
    setIsDirty(false);
    setValidationErrors({});
  }, [initialValues]);

  const parseDisplayString = (str: string): number => {
    const sanitized = str.replace(/[^0-9.-]+/g, "");
    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? 0 : parsed;
  };

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!form.title?.trim()) {
      errors.title = "Title is required";
    } else if (form.title.length < 3) {
      errors.title = "Title must be at least 3 characters long";
    }

    if (!form.description?.trim()) {
      errors.description = "Description is required";
    } else if (form.description.length < 10) {
      errors.description = "Description must be at least 10 characters long";
    }

    if (!form.starting_price || form.starting_price <= 0) {
      errors.starting_price = "Starting price must be greater than 0";
    }

    if (!form.min_increment || form.min_increment <= 0) {
      errors.min_increment = "Minimum increment must be greater than 0";
    } else if (form.min_increment < 0.01) {
      errors.min_increment = "Minimum increment must be at least 0.01";
    }

    if (!form.end_time) {
      errors.end_time = "End time is required";
    } else if (form.end_time <= new Date()) {
      errors.end_time = "End time must be in the future";
    }

    if (!form.category) {
      errors.category = "Category is required";
    }

    const qty = Number(form.quantity);
    if (!Number.isInteger(qty) || qty < 1 || qty > 999) {
      errors.quantity = "Quantity must be a whole number from 1 to 999";
    }

    if (images.length === 0) {
      errors.images = "At least one image is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form, images]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    // Clear validation error for this field
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "starting_price") {
      setDisplayStartingPrice(value);
    } else if (name === "min_increment") {
      setDisplayMinIncrement(value);
    } else if (name === "current_price") {
      setDisplayCurrentPrice(value);
    }

    const numericValue = parseDisplayString(value);
    setForm((prev) => ({ ...prev, [name]: numericValue }));
    setIsDirty(true);
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = parseDisplayString(value);
    const formattedValue = formatNumberForDisplay(numericValue);

    if (name === "starting_price") {
      setDisplayStartingPrice(formattedValue);
    } else if (name === "min_increment") {
      setDisplayMinIncrement(formattedValue);
    } else if (name === "current_price") {
      setDisplayCurrentPrice(formattedValue);
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, category: event.target.value }));
    setIsDirty(true);
    setValidationErrors((prev) => ({ ...prev, category: "" }));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
    const qty = digits === "" ? 0 : parseInt(digits, 10);
    setForm((prev) => ({ ...prev, quantity: qty }));
    setIsDirty(true);
    setValidationErrors((prev) => ({ ...prev, quantity: "" }));
  };

  const openCategoryDialog = (mode: "create" | "edit" | "delete") => {
    setCategoryMsg(null);
    setCategoryNameInput(mode === "create" ? "" : form.category || "");
    setCategoryDialog(mode);
  };

  const handleCategoryDialogSave = async () => {
    if (!categoryDialog) return;
    setCategoryBusy(true);
    setCategoryMsg(null);
    try {
      if (categoryDialog === "create") {
        const res = await api.post(API_ENDPOINTS.CATEGORIES, {
          name: categoryNameInput.trim(),
        });
        await loadCategories();
        setForm((prev) => ({ ...prev, category: res.data.name }));
        setIsDirty(true);
      } else if (categoryDialog === "edit") {
        if (!form.category) throw new Error("No category selected");
        const res = await api.put(
          `${API_ENDPOINTS.CATEGORIES}/${encodeURIComponent(form.category)}`,
          { name: categoryNameInput.trim() }
        );
        await loadCategories();
        setForm((prev) => ({ ...prev, category: res.data.name }));
        setIsDirty(true);
      } else if (categoryDialog === "delete") {
        if (!form.category) throw new Error("No category selected");
        await api.delete(
          `${API_ENDPOINTS.CATEGORIES}/${encodeURIComponent(form.category)}`
        );
        await loadCategories();
        setForm((prev) => ({
          ...prev,
          category: "Other",
        }));
        setIsDirty(true);
      }
      setCategoryDialog(null);
    } catch (err: any) {
      setCategoryMsg(
        err?.response?.data?.detail ||
          err?.message ||
          "Category action failed"
      );
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleParcelIdChange = (event: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, parcel_id: event.target.value }));
    setIsDirty(true);
  };

  const handleEndTimeChange = (date: Date | null) => {
    setForm((prev) => ({ ...prev, end_time: date }));
    setIsDirty(true);
    setValidationErrors((prev) => ({ ...prev, end_time: "" }));
  };

  const handleVideoFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploading(true);
      setFormError(null);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/upload/video", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          throw new Error("Video upload failed");
        }
        const data = await res.json();
        if (data.url) {
          setForm((prev) => ({ ...prev, video: data.url }));
          setVideoFile(file);
          setIsDirty(true);
        } else {
          throw new Error("No video URL returned");
        }
      } catch (err) {
        setFormError("Failed to upload video");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveVideo = () => {
    setForm((prev) => ({ ...prev, video: undefined }));
    setVideoFile(null);
    setIsDirty(true);
  };

  const validateImage = async (file: File): Promise<boolean> => {
    if (file.size > MAX_IMAGE_SIZE) {
      setFormError(
        `Image size must be less than ${MAX_IMAGE_SIZE / 1024 / 1024}MB`
      );
      return false;
    }

    // Skip browser validation for HEIC files (let backend handle it)
    if (
      file.type === "image/heic" ||
      file.name.toLowerCase().endsWith(".heic")
    ) {
      return true;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (
          img.width > MAX_IMAGE_DIMENSION ||
          img.height > MAX_IMAGE_DIMENSION
        ) {
          setFormError(
            `Image dimensions must be less than ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION}px`
          );
          resolve(false);
        } else if (
          img.width < MIN_IMAGE_DIMENSION ||
          img.height < MIN_IMAGE_DIMENSION
        ) {
          setFormError(
            `Image dimensions must be at least ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION}px`
          );
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        setFormError("Invalid image file");
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setFormError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        let file = files[i];

        // File type validation
        if (
          !ALLOWED_IMAGE_TYPES.includes(file.type) &&
          !file.name.toLowerCase().endsWith(".heic")
        ) {
          setFormError("Only JPEG, PNG, GIF, and HEIC images are allowed.");
          continue;
        }

        // Validate image
        const isValid = await validateImage(file);
        if (!isValid) continue;

        // No HEIC to JPEG conversion here; backend will handle it

        const formData = new FormData();
        formData.append("image", file);
        const response = await auctionService.uploadImage(formData);
        if (!response?.image_url) {
          throw new Error("Invalid response from image upload");
        }
        setImages((prev) => [...prev, response.image_url]);
        setIsDirty(true);
      }
    } catch (err) {
      console.error("Image upload error:", err);
      setFormError("Failed to upload image(s)");
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleReset = () => {
    setForm({ ...initialValues, quantity: initialValues.quantity ?? 1 });
    setImages(initialValues.images || []);
    setIsDirty(false);
    setValidationErrors({});
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (
      images.some((img) => typeof img === "string" && img.startsWith("blob:"))
    ) {
      setFormError(
        "Please wait for all images to finish uploading before saving."
      );
      return;
    }
    setFormError(null);
    const filteredImages = images.filter(
      (img) => typeof img === "string" && !img.startsWith("blob:")
    );
    try {
      await onSubmit({ ...form, images: filteredImages });
      if (!isEdit) {
        handleReset();
      }
    } catch (err) {
      setFormError("Failed to save auction. Please try again.");
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? "Edit Auction" : "Create New Auction"}
        </Typography>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                error={!!validationErrors.title}
                helperText={validationErrors.title}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={4}
                value={form.description}
                onChange={handleChange}
                required
                error={!!validationErrors.description}
                helperText={validationErrors.description}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Starting Price"
                  name="starting_price"
                  type="text"
                  value={displayStartingPrice}
                  onChange={handleNumericChange}
                  onBlur={handleNumericBlur}
                  fullWidth
                  required
                  margin="normal"
                  error={!!validationErrors.starting_price}
                  helperText={validationErrors.starting_price}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Minimum Bid Increment"
                  name="min_increment"
                  type="text"
                  value={displayMinIncrement}
                  onChange={handleNumericChange}
                  onBlur={handleNumericBlur}
                  fullWidth
                  required
                  margin="normal"
                  error={!!validationErrors.min_increment}
                  helperText={validationErrors.min_increment}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {isEdit && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Current Price"
                    name="current_price"
                    type="text"
                    value={displayCurrentPrice}
                    onChange={handleNumericChange}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="End Time"
                  value={form.end_time}
                  onChange={handleEndTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!validationErrors.end_time,
                      helperText: validationErrors.end_time,
                    },
                  }}
                  minDateTime={new Date()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Quantity"
                name="quantity"
                value={form.quantity || ""}
                onChange={handleQuantityChange}
                error={!!validationErrors.quantity}
                helperText={
                  validationErrors.quantity || "Whole number, 1–999"
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 3,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <FormControl
                  fullWidth
                  error={!!validationErrors.category}
                  sx={{ flex: 1 }}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={
                      categories.includes(form.category)
                        ? form.category
                        : categories[0] || ""
                    }
                    label="Category"
                    onChange={handleCategoryChange}
                    required
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.category && (
                    <Typography color="error" variant="caption">
                      {validationErrors.category}
                    </Typography>
                  )}
                </FormControl>
                <Tooltip title="Add category">
                  <IconButton
                    color="primary"
                    onClick={() => openCategoryDialog("create")}
                    sx={{ mt: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit category">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={!form.category}
                      onClick={() => openCategoryDialog("edit")}
                      sx={{ mt: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete category">
                  <span>
                    <IconButton
                      color="error"
                      disabled={!form.category || form.category === "Other"}
                      onClick={() => openCategoryDialog("delete")}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>
                  Images ({images.length}/9)
                </Typography>
                <Tooltip title="Images must be JPEG, PNG, GIF, or HEIC (iPhone), between 200x200px and 8000x8000px, and less than 10MB">
                  <InfoIcon color="action" fontSize="small" />
                </Tooltip>
              </Box>
              {validationErrors.images && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ display: "block", mb: 1 }}
                >
                  {validationErrors.images}
                </Typography>
              )}
              <Grid container spacing={2}>
                {images.map((image, index) => (
                  <Grid item key={index} xs={12} sm={6} md={4}>
                    <Card>
                      <Box sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={image}
                          alt={`Auction item ${index + 1}`}
                          sx={{ objectFit: "cover" }}
                        />
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(255, 255, 255, 0.8)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                          }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
                {images.length < 9 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,image/heic"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                        multiple
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        {isUploading ? (
                          <CircularProgress size={40} />
                        ) : (
                          <AddIcon
                            sx={{ fontSize: 40, color: "text.secondary" }}
                          />
                        )}
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          {isUploading ? "Uploading..." : "Add Image"}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isEdit && (
              <Grid item xs={12}>
                <Typography variant="h6">Video</Typography>
                {form.video && !videoFile && (
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={() => window.open(form.video, "_blank")}
                    >
                      View Current Video
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleRemoveVideo}
                      sx={{ ml: 1 }}
                    >
                      Remove Video
                    </Button>
                  </Box>
                )}
                <Box sx={{ mt: 1 }}>
                  <Button variant="contained" component="label">
                    {form.video ? "Upload New Video" : "Upload Video"}
                    <input
                      type="file"
                      hidden
                      accept="video/*"
                      onChange={handleVideoFileChange}
                    />
                  </Button>
                  {videoFile && (
                    <Typography sx={{ ml: 2, display: "inline" }}>
                      {videoFile.name}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            {form.parcel_id && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    window.open(`/admin/gis/${form.parcel_id}`, "_blank")
                  }
                >
                  View GIS Details
                </Button>
              </Grid>
            )}
            {gisProperties && gisProperties.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="gis-property-select-label">
                    GIS Property
                  </InputLabel>
                  <Select
                    labelId="gis-property-select-label"
                    value={form.parcel_id || ""}
                    onChange={handleParcelIdChange}
                    label="GIS Property"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {gisProperties.map((prop) => (
                      <MenuItem key={prop.parcel_id} value={prop.parcel_id}>
                        {prop.parcel_id} - {prop.site_address || "No address"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isUploading || loading || !isDirty}
                  startIcon={
                    loading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {isEdit ? "Save Changes" : "Create Auction"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={handleReset}
                  disabled={isUploading || loading || !isDirty}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => window.history.back()}
                  disabled={isUploading || loading}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Dialog
        open={categoryDialog !== null}
        onClose={() => !categoryBusy && setCategoryDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {categoryDialog === "create" && "Create Category"}
          {categoryDialog === "edit" && "Edit Category"}
          {categoryDialog === "delete" && "Delete Category"}
        </DialogTitle>
        <DialogContent>
          {categoryMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {categoryMsg}
            </Alert>
          )}
          {categoryDialog === "delete" ? (
            <Typography>
              Delete category <strong>{form.category}</strong>? This is only
              allowed if no auctions use it.
            </Typography>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label="Category name"
              value={categoryNameInput}
              onChange={(e) => setCategoryNameInput(e.target.value)}
              inputProps={{ maxLength: 50 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCategoryDialog(null)}
            disabled={categoryBusy}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCategoryDialogSave}
            color={categoryDialog === "delete" ? "error" : "primary"}
            variant="contained"
            disabled={
              categoryBusy ||
              (categoryDialog !== "delete" && !categoryNameInput.trim())
            }
          >
            {categoryBusy
              ? "Saving..."
              : categoryDialog === "delete"
              ? "Delete"
              : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuctionForm;
